import { useMemo } from 'react';

import { DistributionChart, type DistributionDatum } from '@/components/charts/DistributionChart';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  LoadingBlock,
  PageHeader,
  StatTile,
  StatusView,
} from '@/components/ui';
import { computeSessionStats, useSessions } from '@/features/sessions/hooks';
import { formatDuration, formatMode } from '@/lib/formatting';
import { effectiveDurationSec, sessionTimestamp } from '@/features/sessions/model';

import styles from './AnalyticsPage.module.css';

// Short labels: the full wording collides on the x-axis at card width, and a
// dropped tick is worse than an abbreviation.
const DURATION_BUCKETS = [
  { label: '<2m', max: 120 },
  { label: '2–5m', max: 300 },
  { label: '5–10m', max: 600 },
  { label: '10–20m', max: 1200 },
  { label: '20–40m', max: 2400 },
  { label: '40m+', max: Number.POSITIVE_INFINITY },
] as const;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * Aggregate statistics over the session records.
 *
 * Deliberately built on the summary rows rather than the raw EEG files:
 * downloading and decoding every recording to draw one chart would move
 * hundreds of megabytes. Per-recording signal analysis lives on the session
 * detail page, where exactly one file is in hand.
 */
export function AnalyticsPage() {
  const { sessions, isPending, isError, refetch } = useSessions();
  const stats = useMemo(() => computeSessionStats(sessions), [sessions]);

  const durations = useMemo<DistributionDatum[]>(() => {
    const counts = new Array<number>(DURATION_BUCKETS.length).fill(0);
    for (const session of sessions) {
      const duration = effectiveDurationSec(session);
      if (duration === null) continue;
      const index = DURATION_BUCKETS.findIndex((bucket) => duration < bucket.max);
      const safe = index === -1 ? DURATION_BUCKETS.length - 1 : index;
      counts[safe] = (counts[safe] ?? 0) + 1;
    }
    return DURATION_BUCKETS.map((bucket, index) => ({
      label: bucket.label,
      value: counts[index] ?? 0,
    }));
  }, [sessions]);

  const weekdays = useMemo<DistributionDatum[]>(() => {
    const counts = new Array<number>(7).fill(0);
    for (const session of sessions) {
      const timestamp = sessionTimestamp(session);
      if (timestamp === null) continue;
      const day = timestamp.getDay();
      counts[day] = (counts[day] ?? 0) + 1;
    }
    return WEEKDAYS.map((label, index) => ({ label, value: counts[index] ?? 0 }));
  }, [sessions]);

  const hours = useMemo<DistributionDatum[]>(() => {
    const counts = new Array<number>(24).fill(0);
    for (const session of sessions) {
      const timestamp = sessionTimestamp(session);
      if (timestamp === null) continue;
      const hour = timestamp.getHours();
      counts[hour] = (counts[hour] ?? 0) + 1;
    }
    return counts.map((value, hour) => ({ label: `${String(hour).padStart(2, '0')}`, value }));
  }, [sessions]);

  const modes = useMemo<DistributionDatum[]>(
    () => stats.modes.map((entry) => ({ label: formatMode(entry.mode), value: entry.count })),
    [stats.modes],
  );

  if (isError) {
    return (
      <>
        <PageHeader eyebrow="Analysis" title="EEG analytics" />
        <Card>
          <StatusView
            icon="cloud-off"
            tone="danger"
            title="Could not load your sessions"
            action={
              <Button variant="primary" icon="refresh" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          />
        </Card>
      </>
    );
  }

  if (!isPending && sessions.length === 0) {
    return (
      <>
        <PageHeader eyebrow="Analysis" title="EEG analytics" />
        <Card>
          <StatusView
            icon="activity"
            title="Nothing to analyse yet"
            message="Once your account has recorded sessions, their aggregate statistics appear here."
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Analysis"
        title="EEG analytics"
        description="Aggregate patterns across every session on your account. Per-recording signal analysis lives on each session's detail page."
      />

      <div className={styles.stats}>
        <StatTile label="Sessions analysed" icon="list" loading={isPending} value={stats.total} />
        <StatTile
          label="Mean practice"
          icon="clock"
          loading={isPending}
          value={stats.meanDurationSec !== null ? formatDuration(stats.meanDurationSec) : '—'}
          caption="Mean session length"
        />
        <StatTile
          label="Mean score"
          icon="activity"
          mono
          loading={isPending}
          value={stats.meanScore !== null ? stats.meanScore.toFixed(2) : '—'}
          caption={stats.bestScore !== null ? `Peak ${stats.bestScore.toFixed(2)}` : undefined}
        />
        <StatTile
          label="With raw EEG"
          icon="waves"
          loading={isPending}
          value={stats.withRawData}
          caption={`of ${stats.total} sessions`}
        />
      </div>

      {isPending ? (
        <Card>
          <LoadingBlock label="Computing analytics…" />
        </Card>
      ) : (
        <div className={styles.grid}>
          <Card>
            <CardHeader title="Session length" subtitle="How long your sessions run" />
            <CardBody className={styles.chartBody}>
              <DistributionChart data={durations} measure="Sessions" height={220} />
            </CardBody>
            <div className={styles.legend}>
              <span className={styles.swatch} /> Sessions per length band
            </div>
          </Card>

          <Card>
            <CardHeader title="Day of week" subtitle="When you practise" />
            <CardBody className={styles.chartBody}>
              <DistributionChart data={weekdays} measure="Sessions" height={220} />
            </CardBody>
            <div className={styles.legend}>
              <span className={styles.swatch} /> Sessions per weekday
            </div>
          </Card>

          <Card>
            <CardHeader title="Time of day" subtitle="Start hour, local time" />
            <CardBody className={styles.chartBody}>
              <DistributionChart data={hours} measure="Sessions" height={220} />
            </CardBody>
            <div className={styles.legend}>
              <span className={styles.swatch} /> Sessions per hour
            </div>
          </Card>

          {modes.length > 0 ? (
            <Card>
              <CardHeader title="Mode" subtitle="Which practice modes you use" />
              <CardBody className={styles.chartBody}>
                <DistributionChart data={modes} measure="Sessions" height={220} horizontal />
              </CardBody>
              <div className={styles.legend}>
                <span className={styles.swatch} /> Sessions per mode
              </div>
            </Card>
          ) : null}
        </div>
      )}
    </>
  );
}
